'use client';

import { HeadingText } from '@/components/atoms/HeadingText';
import { Badge } from '@/components/Badge';
import {
	IconArrowRight,
	IconHandshake,
	IconHomeDollar,
} from '@/components/icons';
import { SpaceWow } from '@/components/SpaceWow';
import { VideoEmbed } from '@/components/VideoEmbed';
import Link from 'next/link';
import { SellingPoints } from '../SellingPoints';
import { IconOperator } from '@/components/icons';
import { useSplitAndHeap } from '@/hooks/useSplitAndHeap';

interface HVACReplacementHeroProps {
	testId?: string;
}

const HVACReplacementHero = (props: HVACReplacementHeroProps) => {
	const { testId = 'HVACReplacementHero' } = props;

	const { ReplaceFootprint } = useSplitAndHeap('ReplaceFootprint');
	const { treatment } = ReplaceFootprint;
	const replaceFlowHref =
		treatment === 'on' ? '/replace/get-started/' : '/replace/in-person/';

	return (
		<div data-testid={testId} className="hvac-container">
			<section className="mb-10 grid max-w-7xl gap-10 lg:mx-auto lg:mb-14 lg:grid-cols-12 lg:gap-4">
				<div className="lg:col-span-5 lg:flex lg:h-full lg:flex-col lg:justify-center">
					<div className="grid place-items-center lg:place-items-start">
						<Badge color="pink">HVAC Replacement</Badge>
					</div>

					<SpaceWow spacer_04 lg__spacer_02 />

					<HeadingText tag="h2" className="lg:text-start">
						Replace your HVAC
					</HeadingText>

					<SpaceWow spacer_03 lg__spacer_04 />

					<p
						className="text-balance text-center text-xl font-normal tracking-tighter text-black
            lg:text-start lg:text-2xl"
					>
						Get a customized HVAC solution for your home with honest pricing,
						all backed by the best brands in the industry.
					</p>

					<SpaceWow spacer_06 lg__spacer_08 />

					<div className="grid place-items-center gap-3 lg:place-items-start lg:gap-4">
						<Link
							data-testid={testId + '-replace-flow'}
							href={replaceFlowHref}
							className="group flex items-center gap-1 text-xl tracking-tight text-pink-600 hover:text-pink-700
              lg:text-2xl"
						>
							<span>Get a new system</span>
							<IconArrowRight
								className="h-6 w-6 fill-pink-600 group-hover:fill-pink-700 lg:h-7 lg:w-7"
								aria-hidden="true"
							/>
						</Link>
						<Link
							data-testid={testId + '-replace-landing'}
							href="/replace/"
							className="group flex items-center gap-1 text-xl tracking-tight text-slate-500 hover:text-slate-600
							lg:text-2xl"
						>
							<span>See how it works</span>
							<IconArrowRight
								className="h-6 w-6 fill-slate-500 group-hover:fill-slate-600 lg:h-7 lg:w-7"
								aria-hidden="true"
							/>
						</Link>
					</div>
				</div>
				<div className="overflow-clip rounded-2xl lg:col-span-6 lg:col-start-7 lg:rounded-5xl">
					<VideoEmbed
						videoId="hLAKxvMNxoc"
						videoTitle="How replacing your HVAC works (in 60 seconds)"
						videoSettings={{ suggestRelated: false }}
					/>
				</div>
			</section>
			<SellingPoints
				color="pink"
				sellingPoints={[
					{
						heading: 'The right system',
						content:
							'Get only what you need — reliable equipment at an honest price.',
						renderIcon: IconHandshake,
					},
					{
						heading: 'Advisors on your side',
						content:
							'Personalized guidance from our HVAC experts available when you need it.',
						renderIcon: IconOperator,
					},
					{
						heading: 'Flexible financing options',
						content: 'Find the monthly payment that works for your budget.',
						renderIcon: IconHomeDollar,
					},
				]}
			/>
		</div>
	);
};

export default HVACReplacementHero;
